import { QueryClientProvider } from '@tanstack/react-query';
import { configureAuth, CognitoProvider } from '@trading-utils/sso-cognito';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import AppProvider from './contexts/AppContext';

import AxiosWrapper from './components/axios-wrapper/AxiosWrapper';
import { ErrorBoundary } from './components/ErrorBoundary';
import RoutesComponent from './router/RoutesComponent';

import useConfigFetch from './hooks/utils/useConfigFetch';
import useUpdateMetadata from './hooks/utils/useUpdateMetadata';

import useConfigStore from './store/configStore';

import { queryClient } from './api/config/tanstackQuery.config';

import './style/federated-app.css';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

// Import remixicon fonts conditionally in development mode
if (import.meta.env.DEV) {
  import('remixicon/fonts/remixicon.css');
}

export default function FederatedApp() {
  const environmentSettings = useConfigStore((state) => state.environmentSettings);
  useConfigFetch();
  useUpdateMetadata();

  if (!environmentSettings) return null;

  const currentOrigin = window.location.origin;
  const redirectSignIn = currentOrigin;
  const redirectSignOut = `${currentOrigin}/sign-in`;

  configureAuth({
    userPoolId: environmentSettings.USER_POOL_ID,
    userPoolClientId: environmentSettings.USER_POOL_CLIENT_ID,
    region: environmentSettings.AWS_REGION,
    oauth: {
      domain: environmentSettings.OAUTH_DOMAIN,
      redirectSignIn,
      redirectSignOut,
      scopes: ['openid', 'profile', 'email'],
    },
  });

  return (
    <ErrorBoundary>
      <CognitoProvider>
        <AppProvider>
          <AxiosWrapper>
            <QueryClientProvider client={queryClient}>
              <RoutesComponent />
            </QueryClientProvider>
          </AxiosWrapper>
        </AppProvider>
      </CognitoProvider>
    </ErrorBoundary>
  );
}
